/** @format */
/* eslint-disable */ 
import Day from './day';
import dayjs from 'dayjs';

const $_monthPrivate = {
    /**
     * @public
     * @description
     * This will return an object containing a list of the days in the
     * targeted month. We will also return a `firstDayOffset` which will
     * be used to make sure we populate the date grid with the first day in the right 
     * position
     * 
     * @param {Integer} month - the current month
     * @param {Integer} year - the year for the month
     * @param {Object} instance - the active datepicker instance
     * 
     * @returns {Object}
     */
    buildMonthDaysArray(instance, year, month, dayDecorator) {
		const workingDateInt = month + 1;
		const daysInMonthInt = new Date(year, workingDateInt, 0).getDate();
		let list = [];
		let firstDayOffset = 0;
		for (let i = 1; i <= daysInMonthInt; i++) {
			const loopDate = dayjs(new Date(year, month, i)).locale(instance.locale);
			const dayData = Day.getDateObject(loopDate, instance);
			if (typeof dayDecorator === 'function') {
				list.push(Object.assign(dayData, dayDecorator(dayData)));
			} else {
				list.push(dayData);
			}

			if (i === 1) {
				firstDayOffset = dayData.date.dayIndex;
			}
		}

		return {
			list,
			firstDayOffset
		};
	}
};

export const $_monthPublic = {
    /**
     * @public
     * @description
     * Returns an ordered list of months by locale
     * 
     * @param {Object} instance - the active datepicker instance
     * 
     * @returns {Array}
     */
    listByLocale(instance) {
		let months = [];

		for (let i = 0; i <= 11; i++) {
			const incremented = dayjs().add(i, 'month');
            const monthInt = parseInt(incremented.locale(instance.locale).format('M'));
            const monthIntAsString = incremented.locale(instance.locale).format('MM');
			const validNextMonth = instance.maxDisplayMonth != undefined ? monthInt <= instance.maxDisplayMonth : true;
			const validPrevMonth = instance.minDisplayMonth != undefined ? monthInt >= instance.minDisplayMonth : true;

			months.push({
				byLocale: incremented.locale(instance.locale).format('MMMM'),
				monthIndex: monthInt - 1,
                monthInt,
                monthIntAsString, 
				displayForDropdown: validNextMonth && validPrevMonth //could be used for filter but not currently
			});
		}
		return months.sort((a, b) => {
			if (a.monthIndex > b.monthIndex) return 1;
			if (b.monthIndex > a.monthIndex) return -1;

			return 0;
		});
	},

    /**
     * @public
     * @description
     * Returns a decorated object of Month data for active, next and previous
     * 
     * @param {Integer} monthIndex - the index for the current month
     * @param {Integer} year - the year for the month
     * @param {Object} instance - the active datepicker instance
     * 
     * @returns {Object}
     */
	getMonthData(monthIndex, year, instance) {
		try {
			const monthListings = this.listByLocale(instance);
			monthIndex = monthIndex - 1;
			if (monthIndex < 0) monthIndex = 11;
			if (monthIndex > 11) monthIndex = 0;
			const previousMonth = monthIndex === 0 ? 11 : monthIndex - 1;
			const nextMonth = monthIndex === 11 ? 0 : monthIndex + 1;
			const yearForNextMonth = monthIndex === 11 ? year + 1 : year;
			const yearForPreviousMonth = monthIndex === 0 ? year - 1 : year;
			return {
				currentMonth: monthIndex,
				currentMonthName: monthListings[monthIndex].byLocale,
				currentMonthIntString: monthListings[monthIndex].monthIntAsString,
				nextMonth,
				nextMonthName: monthListings[nextMonth].byLocale,
				nextMonthIntString: monthListings[nextMonth].monthIntAsString,
				yearForNextMonth,
				nextIsSelectable: instance.maxYear != undefined ? yearForNextMonth <= instance.maxYear : true,
				previousMonth,
				previousMonthName: monthListings[previousMonth].byLocale,
				previousMonthIntString: monthListings[previousMonth].monthIntAsString,
				yearForPreviousMonth,
				previousIsSelectable: instance.minYear != undefined ? yearForPreviousMonth >= instance.minYear : true
			};
		} catch (e) {
			console.error(e);
		}
        
    },
    
    /**
     * @public
     * @description
     * Returns the days in the given month/year - which can be offsetted
     * by our chosen first day of the week to display
     * 
     * @param {Object} instance - the active datepicker instance
     * @param {Integer} year - the year for the month
     * @param {Integer} firstDayOfWeek - the index for the current month
     * @param {Integer} monthIndex - the index for the current month
     * @param {Integer} chunks - the index for the current month
     * 
     * @returns {Array}
     */
    getDays(instance, year, firstDayOfWeek = 0, monthIndex = 1, chunks = 0) {
		let monthData = this.getMonthData(monthIndex, year, instance);

		let { list: currentMonthList, firstDayOffset: currentMonthFirstDayOffset } = $_monthPrivate.buildMonthDaysArray(instance, year, monthData.currentMonth, () => {
			return {
				classType: 'month-active'
			};
		});

		let daysFromPreviousMonthNeeded = currentMonthFirstDayOffset - firstDayOfWeek;
		if (daysFromPreviousMonthNeeded < 0) daysFromPreviousMonthNeeded = 7 + daysFromPreviousMonthNeeded;

        //To complete the beginning of the calendar, we might need to grab some dates from the previous
        //month and unshift them onto the beginning of the array
		if (daysFromPreviousMonthNeeded > 0) {
			let { list: previoustMonthList } = $_monthPrivate.buildMonthDaysArray(instance, monthData.yearForPreviousMonth, monthData.previousMonth, () => {
				return {
					classType: 'month-previous'
				};
			});

			currentMonthList.unshift(...previoustMonthList.slice(previoustMonthList.length - daysFromPreviousMonthNeeded));
		}

        // Its possible our grid may now contain empty cells, depending on how many cells
        // we have populated with the active month. Lets fill those with dates from the upcoming
        // month.
		const gridSize = currentMonthList.length > 35 ? 42 : 35;

		const daysFromNextMonth = gridSize - currentMonthList.length;

		// if we have filled exactly 4 rows of 7 so far we'll stop there.
		// Otherwise lets finish the grid
		if (daysFromNextMonth < 7) {
			let { list: nextMonthList } = $_monthPrivate.buildMonthDaysArray(instance, monthData.yearForNextMonth, monthData.nextMonth, () => {
				return {
					classType: 'month-next'
				};
			});

			currentMonthList.push(...nextMonthList.splice(0, daysFromNextMonth));
		}

		if (chunks > 0) {
			return currentMonthList.chunk(chunks); //method added to array prototype
		}

		return currentMonthList;
    },
    
    
};

export {$_monthPublic as default};
